/**
 * Quick View Modal
 * Handles opening/closing the quick view modal and loading product data
 */

/**
 * Parse hide_{handle}_{OptionValue} tags from a product and return
 * an array of option values that should be hidden for this product.
 * e.g. tag "hide_rapid-hydration_Orange" → ["Orange"]
 */
function getHiddenVariantValues(product) {
    const hiddenValues = [];
    if (!product || !Array.isArray(product.tags)) return hiddenValues;
    product.tags.forEach(function(tag) {
        if (tag.indexOf('hide_') === 0) {
            const parts = tag.split('_');
            if (parts.length === 3 && parts[0] === 'hide' && parts[1] === product.handle) {
                hiddenValues.push(parts[2]);
            }
        }
    });
    return hiddenValues;
}

/**
 * Filter product variants, removing any whose option1/option2/option3
 * matches a value in the hiddenValues array.
 */
function filterHiddenVariants(variants, hiddenValues) {
    if (!hiddenValues || hiddenValues.length === 0) return variants;
    return variants.filter(function(v) {
        return hiddenValues.indexOf(v.option1) === -1 &&
               hiddenValues.indexOf(v.option2) === -1 &&
               hiddenValues.indexOf(v.option3) === -1;
    });
}

class QuickViewModal {
    constructor() {
        this.modal = document.getElementById('QuickViewModal');
        this.currentProductHandle = null;
        this.currentImages = []; // Currently displayed images (filtered)
        this.allProductImages = []; // All product images (master list)
        this.currentImageIndex = 0;
        this.currentProduct = null; // Store full product data
        this.preloadedImages = new Map(); // Cache for preloaded images
        this.touchStartX = 0;
        this.touchEndX = 0;
        
        if (!this.modal) return;
        
        this.init();
    }

    init() {
        // Handle quick view button clicks
        document.addEventListener('click', (e) => {
            const quickViewButton = e.target.closest('.card__hover-button--quick-view');
            if (quickViewButton) {
                e.preventDefault();
                e.stopPropagation();
                const cardWrapper = quickViewButton.closest('.card-wrapper');
                // Use product handle instead of ID
                const productHandle = cardWrapper?.dataset?.productHandle ||
                    quickViewButton.dataset.productHandle;
                // Get variant ID if available
                const variantId = cardWrapper?.dataset?.variantId ||
                    quickViewButton.dataset.variantId;

                if (productHandle) {
                    this.open(productHandle, variantId);
                }
            }
        });

        // Handle mobile plus button
        document.addEventListener('click', (e) => {
            const mobilePlus = e.target.closest('.card__mobile-plus');
            if (mobilePlus) {
                const cardWrapper = mobilePlus.closest('.card-wrapper');
                const productHandle = cardWrapper?.dataset?.productHandle ||
                    mobilePlus.dataset.productHandle;
                const variantId = cardWrapper?.dataset?.variantId ||
                    mobilePlus.dataset.variantId;

                if (productHandle) {
                    this.open(productHandle, variantId);
                }
            }
        });

        // Handle form submission
        const form = document.getElementById('quick-view-form');
        if (form) {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.handleAddToCart();
            });
        }

        // Handle image navigation
        const nextButton = this.modal?.querySelector('.quick-view-modal__image-nav--next');
        if (nextButton) {
            nextButton.addEventListener('click', () => this.nextImage());
        }

        const prevButton = this.modal?.querySelector('.quick-view-modal__image-nav--prev');
        if (prevButton) {
            prevButton.addEventListener('click', () => this.prevImage());
            // Initialize prev button as hidden
            prevButton.style.display = 'none';
        }

        // Handle touch swipe for mobile
        const imageWrapper = this.modal?.querySelector('.quick-view-modal__image-wrapper');
        if (imageWrapper) {
            imageWrapper.addEventListener('touchstart', (e) => {
                this.touchStartX = e.touches[0].clientX;
            }, { passive: true });

            imageWrapper.addEventListener('touchend', (e) => {
                this.touchEndX = e.changedTouches[0].clientX;
                this.handleSwipe();
            }, { passive: true });
        }

        // Handle variant selection
        this.modal?.addEventListener('change', (e) => {
            if (e.target.type === 'radio' && e.target.closest('#quick-view-options')) {
                this.updateVariant();
            }
        });
    }

    async open(productHandle, preSelectedVariantId = null) {
        if (!this.modal) return;

        this.currentProductHandle = productHandle;
        this.preSelectedVariantId = preSelectedVariantId ? parseInt(preSelectedVariantId) : null;
        this.showLoading();
        this.modal.show();

        // Initialize navigation buttons when modal opens
        this.initializeNavigationButtons();

        try {
            const productData = await this.fetchProductData(productHandle);
            this.populateModal(productData);
        } catch (error) {
            console.error('Error loading product:', error);
            this.showError();
        }
    }

    initializeNavigationButtons() {
        const prevButton = this.modal?.querySelector('.quick-view-modal__image-nav--prev');
        const nextButton = this.modal?.querySelector('.quick-view-modal__image-nav--next');
        
        if (prevButton) {
            prevButton.style.display = 'none';
        }
        if (nextButton) {
            nextButton.style.display = 'flex';
        }
    }

    async fetchProductData(productHandle) {
        // First try to fetch the custom template
        try {
            const response = await fetch(`/products/${productHandle}?view=quick-view`);
            if (response.ok) {
                const html = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                // Extract product data from the HTML
                const productJson = doc.querySelector('[type="application/json"][data-product-json]');
                if (productJson) {
                    return JSON.parse(productJson.textContent);
                }
            }
        } catch (error) {
            console.warn('Custom template not found, using .js endpoint');
        }

        // Fallback: Use Shopify's built-in .js endpoint
        const jsonResponse = await fetch(`/products/${productHandle}.js`);
        if (!jsonResponse.ok) {
            throw new Error('Failed to fetch product');
        }
        const product = await jsonResponse.json();

        // Enhance with metafields if needed (you may need to fetch these separately)
        return product;
    }

    populateModal(product) {
        // Validate product data
        if (!product) {
            console.error('No product data provided');
            this.showError();
            return;
        }

        // Store product data for later use
        this.currentProduct = product;

        // Set title
        const titleEl = document.getElementById('quick-view-title');
        if (titleEl) titleEl.textContent = product.title || '';

        // Store all product images as master list
        this.allProductImages = (product.images && Array.isArray(product.images) && product.images.length > 0)
            ? product.images
            : [];

        // Populate variant options first (needed for variant selection)
        this.populateVariantOptions(product);

        // Determine which variant to use
        let selectedVariant = null;
        if (this.preSelectedVariantId && product.variants && Array.isArray(product.variants)) {
            selectedVariant = product.variants.find(v => v.id === this.preSelectedVariantId);
        }
        if (!selectedVariant && product.variants && product.variants.length > 0) {
            selectedVariant = product.variants[0];
        }

        // Set initial variant
        if (selectedVariant) {
            const variantIdInput = document.getElementById('quick-view-variant-id');
            if (variantIdInput) {
                variantIdInput.value = selectedVariant.id;
            }
            this.updatePrice(selectedVariant);
            this.updateAddToCartButton(selectedVariant);

            // Pre-select the variant options
            this.selectVariantOptions(selectedVariant, product);

            // Filter images for selected variant
            this.filterImagesForVariant(selectedVariant);
        } else {
            // No variant selected, show all images
            this.currentImages = [...this.allProductImages];
            this.currentImageIndex = 0;
            this.updateImage();
        }

        // Set price
        const priceEl = document.getElementById('quick-view-price');
        if (priceEl && selectedVariant) {
            priceEl.innerHTML = this.formatPrice(selectedVariant);
        }

        // Set rating (Yotpo)
        const ratingEl = document.getElementById('quick-view-rating');
        if (ratingEl) {
            ratingEl.innerHTML = `<div class="yotpo bottomLine" data-product-id="${product.id}"></div>`;
            if (window.yotpo) {
                window.yotpo.refreshWidgets();
            }
        }

        // Set description
        const descEl = document.getElementById('quick-view-description');
        if (descEl) {
            // Handle both string and object descriptions
            const description = typeof product.description === 'string'
                ? product.description
                : (product.body_html || '');
            descEl.textContent = description.replace(/<[^>]*>/g, '').substring(0, 200);
        }

        // Set view details link
        const viewDetailsLink = document.getElementById('quick-view-view-details');
        if (viewDetailsLink && selectedVariant) {
            const variantParam = selectedVariant ? `?variant=${selectedVariant.id}` : '';
            viewDetailsLink.href = `/products/${product.handle || this.currentProductHandle}${variantParam}`;
        }

        this.hideLoading();
        // Initialize navigation buttons after modal is populated
        this.updateNavigationButtons();
    }

    /**
     * Filter images for a specific variant
     * Shows variant-specific images if available, otherwise shows default product images
     */
    filterImagesForVariant(variant) {
        // Case 4: No variants - show all default images
        if (!this.currentProduct || this.currentProduct.has_only_default_variant) {
            this.currentImages = [...this.allProductImages];
            this.currentImageIndex = 0;
            this.updateImage();
            return;
        }

        // Case 3: Variant exists but no images available - show default images
        if (!variant || !this.allProductImages || this.allProductImages.length === 0) {
            this.currentImages = [...this.allProductImages];
            this.currentImageIndex = 0;
            this.updateImage();
            return;
        }

        // Get options to determine filtering strategy
        const options = this.currentProduct.options_with_values || this.currentProduct.options;
        const optionCount = options && Array.isArray(options) ? options.length : 0;

        // Case 1: Multiple options (e.g., Flavor + Size) - filter by FIRST option only
        if (optionCount > 1) {
            const firstOptionValue = variant.option1;
            if (firstOptionValue) {
                const filteredImages = this.getImagesByFirstOption(firstOptionValue);
                if (filteredImages && filteredImages.length > 0) {
                    this.currentImages = filteredImages;
                    this.currentImageIndex = 0;
                } else {
                    // No images found for first option, show all default images
                    this.currentImages = [...this.allProductImages];
                    this.currentImageIndex = 0;
                }
            } else {
                // No first option value, show all images
                this.currentImages = [...this.allProductImages];
                this.currentImageIndex = 0;
            }
        }
        // Case 2: Single option (e.g., just Size) - show variant-specific images but keep all in carousel
        else if (optionCount === 1) {
            const variantImages = this.getVariantImages(variant);
            if (variantImages && variantImages.length > 0) {
                // Merge variant images with all images, prioritizing variant images
                const mergedImages = [...variantImages];
                this.allProductImages.forEach(img => {
                    const exists = mergedImages.some(vi =>
                        (vi.id && img.id && vi.id === img.id) ||
                        (vi.src && img.src && vi.src === img.src)
                    );
                    if (!exists) {
                        mergedImages.push(img);
                    }
                });
                this.currentImages = mergedImages;
                // Set index to first variant image if available
                this.currentImageIndex = 0;
            } else {
                // No variant-specific images, show all default images
                this.currentImages = [...this.allProductImages];
                this.currentImageIndex = 0;
            }
        }
        // Fallback: Show all images
        else {
            this.currentImages = [...this.allProductImages];
            this.currentImageIndex = 0;
        }

        this.updateImage();
    }

    /**
     * Get images by first option value (for products with multiple options)
     * Returns all images that match the first option across all second/third options
     */
    getImagesByFirstOption(firstOptionValue) {
        if (!firstOptionValue || !this.allProductImages || !this.currentProduct) {
            return [];
        }

        const matchingImages = [];
        const firstOptionValueLower = (firstOptionValue || '').toLowerCase();

        // Get all variants that match the first option
        const matchingVariants = this.currentProduct.variants.filter(v => {
            return v.option1 && v.option1.toLowerCase() === firstOptionValueLower;
        });

        // Collect all images from matching variants
        const variantImageIds = new Set();
        const variantImageSrcs = new Set();

        matchingVariants.forEach(variant => {
            // Check variant.featured_image
            if (variant.featured_image) {
                if (variant.featured_image.id) {
                    variantImageIds.add(variant.featured_image.id);
                }
                if (variant.featured_image.src) {
                    variantImageSrcs.add(variant.featured_image.src.split('?')[0]);
                }
            }
            // Check variant.image (alternative property)
            if (variant.image) {
                if (variant.image.id) {
                    variantImageIds.add(variant.image.id);
                }
                if (variant.image.src) {
                    variantImageSrcs.add(variant.image.src.split('?')[0]);
                }
            }
        });

        // Find all product images that match any of the variant images
        this.allProductImages.forEach(img => {
            let shouldInclude = false;

            // Match by ID
            if (img.id && variantImageIds.has(img.id)) {
                shouldInclude = true;
            }
            // Match by src (base URL without query params)
            else if (img.src) {
                const imgSrcBase = img.src.split('?')[0];
                variantImageSrcs.forEach(variantSrc => {
                    if (imgSrcBase.includes(variantSrc) || variantSrc.includes(imgSrcBase)) {
                        shouldInclude = true;
                    }
                });
            }
            // Match by alt text containing first option value
            else if (img.alt) {
                const imgAltLower = (img.alt || '').toLowerCase();
                if (imgAltLower.includes(firstOptionValueLower)) {
                    shouldInclude = true;
                }
            }

            if (shouldInclude) {
                matchingImages.push(img);
            }
        });

        // If no direct matches found, try matching by alt text for all images
        if (matchingImages.length === 0) {
            this.allProductImages.forEach(img => {
                const imgAlt = (img.alt || '').toLowerCase();
                if (imgAlt.includes(firstOptionValueLower)) {
                    matchingImages.push(img);
                }
            });
        }

        // Remove duplicates
        const uniqueImages = [];
        const seenIds = new Set();
        matchingImages.forEach(img => {
            const imgId = img.id || img.src;
            if (imgId && !seenIds.has(imgId)) {
                seenIds.add(imgId);
                uniqueImages.push(img);
            }
        });

        return uniqueImages;
    }

    /**
     * Get images associated with a specific variant
     * Returns array of variant-specific images, or empty array if none found
     */
    getVariantImages(variant) {
        if (!variant || !this.allProductImages) {
            return [];
        }

        const variantImages = [];

        // Method 1: Check if variant has featured_image and find matching images
        if (variant.featured_image) {
            const featuredImageId = variant.featured_image.id;
            const featuredImageSrc = variant.featured_image.src;

            // Find all images that match this variant's featured image
            this.allProductImages.forEach(img => {
                if (img.id === featuredImageId ||
                    img.src === featuredImageSrc ||
                    (img.src && featuredImageSrc && img.src.includes(featuredImageSrc.split('?')[0]))) {
                    variantImages.push(img);
                }
            });
        }

        // Method 2: Check variant.image property
        if (variant.image && variantImages.length === 0) {
            const variantImageId = variant.image.id;
            const variantImageSrc = variant.image.src;

            this.allProductImages.forEach(img => {
                if (img.id === variantImageId ||
                    img.src === variantImageSrc ||
                    (img.src && variantImageSrc && img.src.includes(variantImageSrc.split('?')[0]))) {
                    variantImages.push(img);
                }
            });
        }

        // Method 3: Check if images have variant associations in their properties
        if (variantImages.length === 0 && this.currentProduct && this.currentProduct.variants) {
            // Try to find images by matching variant option values in alt text
            const variantOption1 = variant.option1;
            const variantOption2 = variant.option2;
            const variantOption3 = variant.option3;

            this.allProductImages.forEach(img => {
                const imgAlt = (img.alt || '').toLowerCase();
                const option1Lower = (variantOption1 || '').toLowerCase();
                const option2Lower = (variantOption2 || '').toLowerCase();
                const option3Lower = (variantOption3 || '').toLowerCase();

                // Check if image alt contains variant option values
                if (option1Lower && imgAlt.includes(option1Lower)) {
                    variantImages.push(img);
                } else if (option2Lower && imgAlt.includes(option2Lower)) {
                    variantImages.push(img);
                } else if (option3Lower && imgAlt.includes(option3Lower)) {
                    variantImages.push(img);
                }
            });
        }

        // Remove duplicates
        const uniqueImages = [];
        const seenIds = new Set();
        variantImages.forEach(img => {
            const imgId = img.id || img.src;
            if (imgId && !seenIds.has(imgId)) {
                seenIds.add(imgId);
                uniqueImages.push(img);
            }
        });

        return uniqueImages;
    }

    /**
     * Select variant options in the form based on a variant
     */
    selectVariantOptions(variant, product) {
        // Get the options for this variant
        const options = product.options_with_values || product.options;

        if (!options || !variant) return;

        // Select the radio buttons that match this variant
        options.forEach((option, optionIndex) => {
            const optionName = option.name || option;
            let optionValue = null;

            if (optionIndex === 0) {
                optionValue = variant.option1;
            } else if (optionIndex === 1) {
                optionValue = variant.option2;
            } else if (optionIndex === 2) {
                optionValue = variant.option3;
            }

            if (optionValue) {
                // Find and check the radio button for this option value
                const radio = document.querySelector(`input[name="${optionName}"][value="${optionValue}"]`);
                if (radio) {
                    radio.checked = true;
                    // Trigger change event to update variant
                    radio.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        });
    }

    /**
     * Convert RGB color object to hex string
     * Handles both {r, g, b} and {red, green, blue} formats
     */
    rgbToHex(rgb) {
        if (!rgb || typeof rgb !== 'object') return '';

        // Get RGB values (handle both r/g/b and red/green/blue)
        let r = rgb.r !== undefined ? rgb.r : (rgb.red !== undefined ? rgb.red : 0);
        let g = rgb.g !== undefined ? rgb.g : (rgb.green !== undefined ? rgb.green : 0);
        let b = rgb.b !== undefined ? rgb.b : (rgb.blue !== undefined ? rgb.blue : 0);

        // Handle if values are 0-1 range (normalized) instead of 0-255
        if (r <= 1 && g <= 1 && b <= 1 && (r > 0 || g > 0 || b > 0)) {
            r = Math.round(r * 255);
            g = Math.round(g * 255);
            b = Math.round(b * 255);
        }

        // Convert to hex
        const toHex = (n) => {
            const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };

        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }

    /**
     * Populate variant option selectors (size, flavor, etc.)
     */
    populateVariantOptions(product) {
        const optionsContainer = document.getElementById('quick-view-options');

        // Check if container exists first
        if (!optionsContainer) {
            console.warn('Quick view options container not found');
            return;
        }

        // Check if product has only default variant
        if (product.has_only_default_variant) {
            optionsContainer.innerHTML = '';
            return;
        }

        let html = '';

        // Get options - handle both formats
        const options = product.options_with_values || product.options;

        // Check if options exist and is an array
        if (!options || !Array.isArray(options) || options.length === 0) {
            optionsContainer.innerHTML = '';
            return;
        }

        // Build list of option values to hide based on product tags
        const hiddenValues = getHiddenVariantValues(product);
        const filteredVariants = filterHiddenVariants(product.variants || [], hiddenValues);

        // Determine which variant to pre-select (from filtered list only)
        let preSelectedVariant = null;
        if (this.preSelectedVariantId && filteredVariants.length > 0) {
            preSelectedVariant = filteredVariants.find(v => v.id === this.preSelectedVariantId);
        }
        if (!preSelectedVariant && filteredVariants.length > 0) {
            preSelectedVariant = filteredVariants[0];
        }

        options.forEach((option, optionIndex) => {
            const optionName = option.name || option;
            const rawOptionValues = option.values || [];
            const optionValues = hiddenValues.length > 0
                ? rawOptionValues.filter(v => hiddenValues.indexOf(v) === -1)
                : rawOptionValues;

            // Skip if no values
            if (!optionValues || optionValues.length === 0) {
                return;
            }

            const isFlavor = optionName.toLowerCase().includes('flavor');

            html += `
                <div class="quick-view-modal__option-group">
                    <label class="quick-view-modal__option-label">${optionName}</label>
                    <div class="quick-view-modal__option-values">
            `;

            optionValues.forEach((value, valueIndex) => {
                const optionId = `quick-view-${optionName}-${valueIndex}`;

                // Determine if this option should be checked
                let checked = '';
                if (preSelectedVariant) {
                    if (optionIndex === 0 && preSelectedVariant.option1 === value) {
                        checked = 'checked';
                    } else if (optionIndex === 1 && preSelectedVariant.option2 === value) {
                        checked = 'checked';
                    } else if (optionIndex === 2 && preSelectedVariant.option3 === value) {
                        checked = 'checked';
                    }
                } else if (valueIndex === 0) {
                    // Default to first option if no pre-selection
                    checked = 'checked';
                }

                // Find variant to get flavor color
                let flavorColor = '';
                if (isFlavor && filteredVariants.length > 0) {
                    // Find variant that matches this option value
                    const variant = filteredVariants.find(v => {
                        if (optionIndex === 0) return v.option1 === value;
                        if (optionIndex === 1) return v.option2 === value;
                        return v.option3 === value;
                    });

                    // Check for metafields in different possible locations
                    if (variant) {
                        const rawFlavorColor = variant.metafields?.custom?.flavor_color ||
                            variant.metafield?.custom?.flavor_color ||
                            '';

                        // Extract string value from flavor_color (handle both string and object)
                        if (rawFlavorColor) {
                            if (typeof rawFlavorColor === 'string') {
                                // Already a string (hex or color name)
                                flavorColor = rawFlavorColor.trim();
                            } else if (typeof rawFlavorColor === 'object' && rawFlavorColor !== null) {
                                // Handle Shopify Color metafield object format
                                // Try to get hex value first, then convert RGB if needed
                                if (rawFlavorColor.value && typeof rawFlavorColor.value === 'string') {
                                    flavorColor = rawFlavorColor.value.trim();
                                } else if (rawFlavorColor.hex && typeof rawFlavorColor.hex === 'string') {
                                    flavorColor = rawFlavorColor.hex.trim();
                                } else if (rawFlavorColor.color && typeof rawFlavorColor.color === 'string') {
                                    flavorColor = rawFlavorColor.color.trim();
                                } else if ((rawFlavorColor.r !== undefined || rawFlavorColor.red !== undefined) &&
                                    (rawFlavorColor.g !== undefined || rawFlavorColor.green !== undefined) &&
                                    (rawFlavorColor.b !== undefined || rawFlavorColor.blue !== undefined)) {
                                    // Convert RGB object to hex
                                    flavorColor = this.rgbToHex(rawFlavorColor);
                                } else {
                                    // Debug: log unexpected format
                                    console.log('Flavor color object structure:', rawFlavorColor);
                                    console.log('Variant:', variant);
                                    // Try to extract any string property
                                    for (const key in rawFlavorColor) {
                                        if (typeof rawFlavorColor[key] === 'string' && rawFlavorColor[key].trim()) {
                                            flavorColor = rawFlavorColor[key].trim();
                                            break;
                                        }
                                    }
                                }
                            } else {
                                flavorColor = String(rawFlavorColor).trim();
                            }
                        }

                        // Debug logging (remove after testing)
                        if (isFlavor && !flavorColor) {
                            console.log('No flavor color found for:', value, 'Variant:', variant);
                            console.log('Raw flavor color:', rawFlavorColor);
                        }
                    } else {
                        // Debug: variant not found
                        console.log('Variant not found for option:', optionName, 'value:', value, 'index:', optionIndex);
                    }
                }

                const sizeClass = optionName.toLowerCase().includes('size') ? 'quick-view-modal__option--size' : '';
                const flavorClass = isFlavor ? 'quick-view-modal__option--flavor' : '';

                html += `
                    <input
                        type="radio"
                        id="${optionId}"
                        name="${optionName}"
                        value="${value}"
                        ${checked}
                        class="visually-hidden"
                    >
                    <label
                        for="${optionId}"
                        class="quick-view-modal__option ${sizeClass} ${flavorClass}"
                        ${flavorColor ? `style="--flavor-color: ${flavorColor}"` : ''}
                    >
                        ${isFlavor && flavorColor ? `<span class="flavor-color-circle" style="background-color: ${flavorColor};"></span>` : ''}
                        ${value}
                    </label>
                `;
            });

            html += `
                    </div>
                </div>
            `;
        });

        // Only set innerHTML if we have content and container exists
        if (html && optionsContainer) {
            optionsContainer.innerHTML = html;

            // Store product variants JSON
            const script = document.createElement('script');
            script.type = 'application/json';
            script.id = 'quick-view-variants-json';
            script.textContent = JSON.stringify(filteredVariants);
            optionsContainer.appendChild(script);
        } else if (optionsContainer) {
            optionsContainer.innerHTML = '';
        }
    }

    updateVariant() {
        const form = document.getElementById('quick-view-form');
        if (!form) return;

        const formData = new FormData(form);
        const selectedOptions = {};

        form.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
            selectedOptions[radio.name] = radio.value;
        });

        const variantsJson = document.getElementById('quick-view-variants-json');
        if (!variantsJson) return;

        const variants = JSON.parse(variantsJson.textContent);
        const selectedVariant = variants.find(variant => {
            const optionKeys = Object.keys(selectedOptions);
            return (
                variant.option1 === selectedOptions[optionKeys[0]] &&
                variant.option2 === (selectedOptions[optionKeys[1]] || variant.option2) &&
                variant.option3 === (selectedOptions[optionKeys[2]] || variant.option3)
            );
        });

        if (selectedVariant) {
            document.getElementById('quick-view-variant-id').value = selectedVariant.id;
            this.updatePrice(selectedVariant);
            this.updateAddToCartButton(selectedVariant);

            // Filter and update images for selected variant
            this.filterImagesForVariant(selectedVariant);
        }
    }

    updatePrice(variant) {
        const priceEl = document.getElementById('quick-view-price');
        if (priceEl) {
            priceEl.innerHTML = this.formatPrice(variant);
        }
    }

    formatPrice(variant) {
        const price = variant.price / 100; // Convert cents to dollars
        const comparePrice = variant.compare_at_price ? variant.compare_at_price / 100 : null;

        if (comparePrice && comparePrice > price) {
            return `
                <span class="price-item price-item--sale">${this.formatMoney(price)}</span>
                <span class="price-item price-item--regular"><s>${this.formatMoney(comparePrice)}</s></span>
            `;
        }
        return `<span class="price-item">${this.formatMoney(price)}</span>`;
    }

    formatMoney(amount) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    }

    updateAddToCartButton(variant) {
        const button = document.getElementById('quick-view-add-to-cart');
        if (!button) return;

        if (variant.available) {
            button.disabled = false;
            button.textContent = 'ADD TO CART';
        } else {
            button.disabled = true;
            button.textContent = 'OUT OF STOCK';
        }
    }

    /**
     * Preload images for faster navigation
     */
    preloadImages() {
        if (!this.currentImages || this.currentImages.length === 0) return;
        
        // Preload current, next, and previous images
        const indicesToPreload = [
            this.currentImageIndex,
            (this.currentImageIndex + 1) % this.currentImages.length,
            (this.currentImageIndex - 1 + this.currentImages.length) % this.currentImages.length
        ];
        
        indicesToPreload.forEach(index => {
            const image = this.currentImages[index];
            if (!image) return;
            
            const imageUrl = typeof image === 'string' ? image : (image.src || '');
            if (!imageUrl || this.preloadedImages.has(imageUrl)) return;
            
            // Create Image object to preload
            const img = new Image();
            img.src = imageUrl;
            this.preloadedImages.set(imageUrl, img);
        });
    }

    updateImage() {
        if (this.currentImages.length === 0) {
            // No images available, set a placeholder or return
            const imageEl = document.getElementById('quick-view-image');
            if (imageEl) {
                imageEl.src = '';
                imageEl.alt = '';
                imageEl.srcset = '';
            }
            this.updateImageDots();
            this.preloadImages(); // Preload adjacent images
            this.updateNavigationButtons();
            return;
        }

        // Ensure currentImageIndex is within bounds
        if (this.currentImageIndex >= this.currentImages.length) {
            this.currentImageIndex = 0;
        }
        if (this.currentImageIndex < 0) {
            this.currentImageIndex = 0;
        }

        const imageEl = document.getElementById('quick-view-image');
        if (!imageEl) return;

        // Add loading class
        imageEl.style.opacity = '0.5';
        
        // Handle image load
        const handleImageLoad = () => {
            imageEl.style.opacity = '1';
            imageEl.removeEventListener('load', handleImageLoad);
        };
        
        imageEl.addEventListener('load', handleImageLoad);

        const currentImage = this.currentImages[this.currentImageIndex];

        // Check if currentImage exists
        if (!currentImage) {
            // Fallback to first image if current index is invalid
            if (this.currentImages.length > 0) {
                this.currentImageIndex = 0;
                const firstImage = this.currentImages[0];
                if (typeof firstImage === 'string') {
                    imageEl.src = firstImage;
                } else {
                    imageEl.src = firstImage.src || firstImage || '';
                    imageEl.alt = firstImage.alt || '';
                    imageEl.srcset = this.generateSrcSet(firstImage);
                }
            } else {
                imageEl.src = '';
                imageEl.alt = '';
                imageEl.srcset = '';
            }
            this.updateImageDots();
            this.preloadImages(); // Preload after update
            return;
        }

        // Handle different image formats
        if (typeof currentImage === 'string') {
            imageEl.src = currentImage;
            imageEl.alt = '';
            imageEl.srcset = '';
        } else {
            imageEl.src = currentImage.src || currentImage || '';
            imageEl.alt = currentImage.alt || '';
            imageEl.srcset = this.generateSrcSet(currentImage);
        }

        this.updateImageDots();
        this.preloadImages(); // Preload adjacent images
        this.updateNavigationButtons(); // Preload adjacent images
    }

    handleSwipe() {
        const swipeThreshold = 50; // Minimum distance for swipe
        const diff = this.touchStartX - this.touchEndX;

        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0) {
                // Swipe left - next image
                this.nextImage();
            } else {
                // Swipe right - previous image
                this.prevImage();
            }
        }
    }

    prevImage() {
        if (this.currentImages.length <= 1) return;
        this.currentImageIndex = (this.currentImageIndex - 1 + this.currentImages.length) % this.currentImages.length;
        this.updateImage();
        this.updateImageDots();
        this.updateNavigationButtons();
    }

    nextImage() {
        if (this.currentImages.length <= 1) return;
        this.currentImageIndex = (this.currentImageIndex + 1) % this.currentImages.length;
        this.updateImage();
        this.updateImageDots();
        this.updateNavigationButtons();
    }

    updateNavigationButtons() {
        const prevButton = this.modal?.querySelector('.quick-view-modal__image-nav--prev');
        const nextButton = this.modal?.querySelector('.quick-view-modal__image-nav--next');
        
        // Check if we're on mobile (buttons should be hidden)
        const isMobile = window.innerWidth <= 989;
        if (isMobile) {
            if (prevButton) prevButton.style.display = 'none';
            if (nextButton) nextButton.style.display = 'none';
            return;
        }
        
        if (!prevButton || !nextButton || this.currentImages.length <= 1) {
            if (prevButton) prevButton.style.display = 'none';
            if (nextButton) nextButton.style.display = 'none';
            return;
        }

        // Show previous button if not on first image
        if (this.currentImageIndex > 0) {
            prevButton.style.display = 'flex';
        } else {
            prevButton.style.display = 'none';
        }

        // Show next button if not on last image
        if (this.currentImageIndex < this.currentImages.length - 1) {
            nextButton.style.display = 'flex';
        } else {
            nextButton.style.display = 'none';
        }
    }

    generateSrcSet(image) {
        if (typeof image === 'string') return '';

        // Reduced sizes for faster loading
        const sizes = [360, 533, 720, 940, 1024];
        const imageSrc = image.src || image;
        const width = image.width || 1024;

        // Extract base URL without size parameters
        const baseUrl = imageSrc.split('?')[0];

        return sizes
            .filter(size => width >= size)
            .map(size => {
                // If the image URL already has size parameters, replace them
                // Otherwise, add size parameter
                if (imageSrc.includes('?width=') || imageSrc.includes('_')) {
                    // Replace existing size or add width parameter
                    const urlWithoutParams = baseUrl;
                    return `${urlWithoutParams}?width=${size} ${size}w`;
                } else {
                    return `${baseUrl}?width=${size} ${size}w`;
                }
            })
            .join(', ');
    }

    updateImageDots() {
        const dotsContainer = this.modal?.querySelector('.quick-view-modal__image-dots');
        if (!dotsContainer || this.currentImages.length <= 1) {
            if (dotsContainer) dotsContainer.innerHTML = '';
            return;
        }

        let html = '';
        this.currentImages.forEach((_, index) => {
            html += `<span class="quick-view-modal__dot ${index === this.currentImageIndex ? 'active' : ''}"></span>`;
        });
        dotsContainer.innerHTML = html;
    }

    nextImage() {
        if (this.currentImages.length <= 1) return;
        this.currentImageIndex = (this.currentImageIndex + 1) % this.currentImages.length;
        this.updateImage();
        this.updateImageDots();
    }

    async handleAddToCart() {
        const variantId = document.getElementById('quick-view-variant-id')?.value;
        if (!variantId) return;
    
        const button = document.getElementById('quick-view-add-to-cart');
        if (!button) return;
    
        button.disabled = true;
        button.textContent = 'Adding...';
    
        // Helper function to extract inner HTML from section HTML
        const getSectionInnerHTML = (html, selector = '.shopify-section') => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const element = doc.querySelector(selector);
            return element ? element.innerHTML : '';
        };
    
        const sectionsToRender = ['cart-icon-bubble', 'mini-cart-popup'];
        const body = JSON.stringify({
            items: [{
                id: variantId,
                quantity: 1
            }],
            sections: sectionsToRender,
            sections_url: window.location.pathname
        });
    
        try {
            const response = await fetch(`${routes.cart_add_url}`, {
                ...fetchConfig('javascript'),
                body: body
            });
    
            if (!response.ok) {
                throw new Error('Failed to add to cart');
            }
    
            const parsedState = await response.json();
    
            // Update cart-icon-bubble if section exists
            if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
                const cartIconBubble = document.getElementById('cart-icon-bubble');
                if (cartIconBubble) {
                    cartIconBubble.innerHTML = getSectionInnerHTML(parsedState.sections['cart-icon-bubble'], '.shopify-section');
                }
            }
    
            // Update mini-cart-popup if section exists
            if (parsedState.sections && parsedState.sections['mini-cart-popup']) {
                const miniCartPopup = document.getElementById('mini-cart-popup');
                if (miniCartPopup) {
                    miniCartPopup.innerHTML = getSectionInnerHTML(parsedState.sections['mini-cart-popup'], '.shopify-section');
    
                    // Update minicart-items class to handle empty state
                    setTimeout(() => {
                        const minicartItems = miniCartPopup.querySelector('minicart-items');
                        if (minicartItems) {
                            if (parsedState.item_count === 0) {
                                minicartItems.classList.add('is-empty');
                            } else {
                                minicartItems.classList.remove('is-empty');
                            }
                        }
                    }, 0);
                }
            }
    
            // Update cart count if function exists
            if (window.updateCartCount) {
                window.updateCartCount();
            }
    
            // Close modal
            this.modal.hide();
    
            // Trigger cart popup if available
            const cartTrigger = document.querySelector('.cart-popup-offcanvas-js-trigger');
            if (cartTrigger) {
                setTimeout(() => cartTrigger.click(), 300);
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            button.textContent = 'Error - Try Again';
            setTimeout(() => {
                button.disabled = false;
                button.textContent = 'ADD TO CART';
            }, 2000);
        }
    }

    showLoading() {
        const loading = this.modal?.querySelector('.quick-view-modal__loading');
        const content = this.modal?.querySelector('.quick-view-modal__content-wrapper');
        if (loading) loading.style.display = 'flex';
        if (content) content.style.display = 'none';
    }

    hideLoading() {
        const loading = this.modal?.querySelector('.quick-view-modal__loading');
        const content = this.modal?.querySelector('.quick-view-modal__content-wrapper');
        if (loading) loading.style.display = 'none';
        if (content) content.style.display = 'flex';
    }

    showError() {
        const content = this.modal?.querySelector('.quick-view-modal__content-wrapper');
        if (content) {
            content.innerHTML = '<p style="padding: 40px; text-align: center;">Error loading product. Please try again.</p>';
            content.style.display = 'block';
        }
        this.hideLoading();
    }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new QuickViewModal();
    });
} else {
    new QuickViewModal();
}